'use client';

import AudioPlayer from 'components/audio-player';
import NoImage from 'assets/icons/no-image.svg';
import SanityImage from '@sanity-image';
import styles from './playlist-display.module.scss';
import { useState } from 'react';
import ReadMoreWrapper from 'components/containers/read-more-wrapper';
import RichText from 'components/rich-text';
import AudioGuideHeader from '../audio-guide-header';
import Breadcrumbs from 'components/breadcrumbs';
import RightsRestrictedMessage from 'components/collection/rights-restricted-message';
import PageSection from 'components/containers/page-section';
import { notFound } from 'next/navigation';
import { type AudioGuidePlaylist } from 'src/app/[locale]/(navigation)/audio-guide/[stopNumber]/data';

const getLocalTranscriptOrEnglish = (track) => {
	if (track.transcript?.length) {
		return track.transcript;
	}

	if (track.transcriptEN?.length) {
		return track.transcriptEN[0]?.transcript || [];
	}

	return [];
};

const formatTracks = (tracks, exhibition, _translations) => {
	return (
		tracks
			?.filter((x) => !!x)
			.map((track) => {
				return {
					audio: track?.audioFile?.url,
					crdImage: track.crdImage,
					description: track.description,
					exhibitionId:
						track?.exhibition?._id ||
						track?.exhibition?._ref ||
						exhibition?._id ||
						exhibition?._ref ||
						null,
					exhibitionTitle:
						track?.exhibition?.title || exhibition?.title || null,
					id: track._id,
					image: track.image,
					title: track.title,
					transcript: getLocalTranscriptOrEnglish(track),
					translations: _translations,
				};
			}) || []
	);
};

const PlaylistDisplay = (props: IProps) => {
	const {
		_id,
		description,
		exhibition,
		title = '',
		tracks,
		_translations,
	} = props.playlist || {};
	const [currentTrack, setCurrentTrack] = useState(tracks?.[0]);

	if (!_id) {
		return notFound();
	}

	const {
		stopNumber = '',
		pageTitle = 'Audio Guide',
		locale,
		showBreadcrumbs = true,
		displayLanguageFilter = false,
		replaceRoute,
		pageUrl,
	} = props;

	const trackChangeCallback = (audioPlayerState) => {
		audioPlayerState?.currentTrack &&
			setCurrentTrack(audioPlayerState.currentTrack);
	};

	//Check to see if either image (from sanity) or crdImageUrl (from CRD) is set.
	const showImage = !!currentTrack?.image || !!currentTrack?.crdImage?.url;

	//If image is from CRD
	const showCrdImage = !!currentTrack?.crdImage;

	//If image is a thumbnail and can't be enlarged
	//All thumbnail images are OASC
	//OASC is public domain... or Bugs Bunny (OASCawy wabbit)
	const isThumbnail =
		showCrdImage &&
		!currentTrack.crdImage?.isOasc &&
		currentTrack.crdImage?.isThumbnail;
	const isRestricted = !currentTrack.crdImage?.isOasc;

	//Format Track Data for <AudioPlayer/>
	const trackData = formatTracks(tracks, exhibition, _translations);

	return (
		<main>
			{showBreadcrumbs && <Breadcrumbs />}
			<AudioGuideHeader
				title={pageTitle}
				stopNumber={stopNumber}
				key={`header-${locale}`}
				displayLanguageFilter={displayLanguageFilter}
				replaceRoute={replaceRoute}
				pageUrl={pageUrl}
			/>
			<PageSection>
				<div className={styles.mainWrapper}>
					{showImage ? (
						<div
							id="artworkContainer"
							className={`${styles.artwork} ${isThumbnail ? styles.artworkThumbnail : ''}`}
						>
							{showCrdImage ? (
								<div className={styles.crdImageWrapper}>
									{/* eslint-disable @next/next/no-img-element */}
									<img
										loading="lazy"
										className={styles.artworkImage}
										src={currentTrack.crdImage.url}
										alt={currentTrack.crdImage.altText}
									/>
									{/* eslint-enable @next/next/no-img-element */}
								</div>
							) : (
								<SanityImage
									image={currentTrack.image}
									sizes={'(max-width: 80px) 90vw, 40vw'}
								/>
							)}
							{isRestricted && <RightsRestrictedMessage />}
						</div>
					) : (
						<div className={styles.artwork}>
							<NoImage className={styles.noImageSvg} />
						</div>
					)}
					<div className={styles.playerWrapper}>
						<div className={styles.descriptionWrapper}>
							{!!title && <h3 className={styles.descriptionTitle}>{title}</h3>}
							{description?.length > 0 && (
								<ReadMoreWrapper maxLines={2}>
									<RichText value={description} />
								</ReadMoreWrapper>
							)}
						</div>
						<section>
							{!!tracks?.length && (
								<AudioPlayer
									id={_id}
									exhibitionId={props.playlist?.exhibition?._id}
									exhibitionTitle={props.playlist?.exhibition?.title}
									tracks={trackData}
									options={{ hasImage: false, transcriptDefaultClosed: true }}
									trackChangeCallback={trackChangeCallback}
									translations={_translations}
								/>
							)}
						</section>
					</div>
				</div>
			</PageSection>
		</main>
	);
};

interface IProps {
	locale?: string;
	playlist: AudioGuidePlaylist;
	stopNumber?: string;
	pageTitle?: string;
	showBreadcrumbs?: boolean;
	displayLanguageFilter?: boolean;
	replaceRoute?: boolean;
	pageUrl?: string;
}

export default PlaylistDisplay;
